// Audio reactor — main-thread side of the AudioWorklet contract. Owns the
// AudioContext + AudioWorkletNode lifecycle, forwards parameters and cursor
// state to the worklet via its port. The worklet processor lives at
// `/audio-worklet.js` (in `public/`) — AudioWorklet modules are loaded as
// classic-style scripts, so the Vite TS pipeline doesn't apply. Plain JS
// in `public/` is the simplest correct path.

import type { Parameters } from '../lib/parameters-schema';

const PROCESSOR_URL = '/audio-worklet.js';

export interface AudioClock {
  currentTime: number;
  baseLatency: number;
  outputLatency: number;
  epochTime: number;
}

export interface AudioReactor {
  arm(): Promise<void>;
  setParameters(params: Parameters): void;
  setMaster(master: number): void;
  setCursor(x: number, y: number, pressure: number): void;
  getClock(): AudioClock | null;
  destroy(): void;
}

export function createAudioReactor(visitorId: string): AudioReactor {
  let ctx: AudioContext | null = null;
  let node: AudioWorkletNode | null = null;
  let cachedParams: Parameters | null = null;
  let epochTime = 0;

  const arm = async (): Promise<void> => {
    if (node) {
      // Already armed; ensure context resumed (Safari can suspend it on tab change)
      if (ctx && ctx.state === 'suspended') await ctx.resume();
      return;
    }
    const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext) as typeof AudioContext;
    ctx = new Ctor({ latencyHint: 'interactive' });
    if (ctx.state === 'suspended') await ctx.resume();
    epochTime = ctx.currentTime;

    await ctx.audioWorklet.addModule(PROCESSOR_URL);
    node = new AudioWorkletNode(ctx, 'herminator-processor', {
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: { visitorId },
    });
    node.connect(ctx.destination);
    node.port.postMessage({ type: 'arm' });
    if (cachedParams) {
      node.port.postMessage({ type: 'params', payload: paramsToVoice(cachedParams) });
    }
  };

  const setParameters = (params: Parameters): void => {
    cachedParams = params;
    if (node) node.port.postMessage({ type: 'params', payload: paramsToVoice(params) });
  };

  // Master target is snapped on `arm`/`mute` messages in the worklet — `params`
  // updates only mutate `params.master`. To actually change the playing volume
  // we both update params.master AND re-arm to snap masterTarget to it.
  const setMaster = (master: number): void => {
    if (!node) return;
    const m = Math.max(0, Math.min(1, master));
    node.port.postMessage({ type: 'params', payload: { master: m } });
    node.port.postMessage({ type: 'arm' });
  };

  const setCursor = (x: number, y: number, pressure: number): void => {
    if (node) node.port.postMessage({ type: 'cursor', payload: { x, y, pressure } });
  };

  const getClock = (): AudioClock | null => {
    if (!ctx) return null;
    return {
      currentTime: ctx.currentTime,
      baseLatency: ctx.baseLatency,
      outputLatency: ctx.outputLatency,
      epochTime,
    };
  };

  const destroy = (): void => {
    try { node?.disconnect(); } catch { /* noop */ }
    void ctx?.close();
    node = null; ctx = null;
  };

  return { arm, setParameters, setMaster, setCursor, getClock, destroy };
}

// Master is omitted intentionally — partial param refreshes must not clobber
// the runtime master (which arcade ducks via setMaster). The worklet keeps its
// constructor default (0.42) until setMaster overrides.
function paramsToVoice(p: Parameters): {
  bpm: number; agitation: number; hueShift: number; moshIntensity: number;
} {
  return {
    bpm: p.beat_bpm,
    agitation: p.agitation,
    hueShift: p.hue_shift,
    moshIntensity: p.mosh_intensity,
  };
}
